import MenuIcon from "@mui/icons-material/Menu";
import {
  AppBar,
  Avatar,
  Box,
  Button,
  Collapse,
  Divider,
  Drawer,
  FormControl,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Menu,
  MenuItem,
  Select,
  Toolbar,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import AccessDashboardButton from "../AccessDashboardButton/AccessDashboardButton";
import LanguageLabel from "../LanguageToggle/LanguageLabel";

const NavBar = ({ isLoggedIn, profileName, onLogout }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [servicesAnchorEl, setServicesAnchorEl] = useState(null);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const [feedbackAnchorEl, setFeedbackAnchorEl] = useState(null);
  const [mobileFeedbackOpen, setMobileFeedbackOpen] = useState(false);
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const direction = i18n.dir();
  const currentLanguage = i18n.language?.split("-")[0];
  const selectedLanguage = ["ar", "de", "en"].includes(currentLanguage)
    ? currentLanguage
    : "en";
  const roleName = useSelector((state) => state.auth.user.roleName);
  const roleNames = useSelector((state) => state.auth.user.roleNames);
  const normalizedRoles = [
    ...(Array.isArray(roleNames) ? roleNames : []),
    roleName,
  ]
    .filter(Boolean)
    .map((role) =>
      String(role).trim().toUpperCase().replace(/[\s-]+/g, "_")
    );
  const profilePath = normalizedRoles.some((role) =>
    ["TEACHER", "TEACHER_MANAGER"].includes(role)
  )
    ? "/teacher-profile"
    : "/student/profile";
  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleProfileNavigation = () => {
    handleMenuClose();
    setDrawerOpen(false);
    navigate(profilePath);
  };

  const handleLanguageChange = (language) => {
    i18n.changeLanguage(language);
  };

  // Close all dropdowns on scroll
  useEffect(() => {
    const handleScroll = () => {
      if (anchorEl) handleMenuClose();
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [anchorEl]);

  const handleLogout = () => {
    onLogout();
    handleMenuClose();
    // navigate("/login");
  };

  const getInitials = (name) => {
    if (!isLoggedIn || !name) return "";
    const initials = name.split(" ");
    return initials.length > 1
      ? `${initials[0][0]}${initials[1][0]}`
      : initials[0][0];
  };

  const navItems = [
    { label: t("navbar.aboutPlatform"), link: "/about-us" },
    { label: t("navbar.team", { defaultValue: "Team" }), link: "/team" },
    { label: t("navbar.contactUs"), link: "/contact" },
  ];

  const serviceItems = [
    { label: t("about.offers.language.title"), link: "/german-language" },
    { label: t("about.offers.university.title"), link: "/university-admission", comingSoon: true },
    { label: t("about.offers.ausbildung.title"), link: "/ausbildung" },
    { label: t("about.offers.graduateJobSearch.title"), link: "/graduate-job-search", comingSoon: true },
    { label: t("about.offers.corporateTraining.title"), link: "/corporate-language-cultural-training" },
    { label: t("about.offers.corporateRecruitment.title"), link: "/corporate-recruitment" },
    { label: t("navbar.events"), link: "/events" },
  ];
  const feedbackItems = [
    { label: t("feedbackNav.rating", { defaultValue: selectedLanguage === "de" ? "Website bewerten" : selectedLanguage === "ar" ? "تقييم الموقع" : "Rate our website" }), link: "/rate-website" },
    { label: t("feedbackNav.problem", { defaultValue: selectedLanguage === "de" ? "Technisches Problem melden" : selectedLanguage === "ar" ? "الإبلاغ عن مشكلة تقنية" : "Report a technical problem" }), link: "/technical-support" },
  ];
  const feedbackLabel = t("feedbackNav.title", { defaultValue: selectedLanguage === "de" ? "Feedback & Hilfe" : selectedLanguage === "ar" ? "التقييم والدعم" : "Feedback & Support" });

  const navigateToService = (link) => {
    setServicesAnchorEl(null);
    setDrawerOpen(false);
    navigate(link);
  };

  return (
    <>
      <AppBar
        position="static"
        sx={{
          height: { xs: 64, sm: 72, md: 80, lg: 100 },
          backgroundColor: "white",
          display: "flex",
          justifyContent: "center",
          direction,
        }}
      >
        <Toolbar
          sx={{
            justifyContent: "space-between",
            minHeight: { xs: 64, sm: 72, md: 80, lg: 100 },
            px: { xs: 1.5, sm: 2, md: 3 },
          }}
        >
          {/* Left Side: Logo */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              height: "100%",
            }}
          >
            <Box
              component={Link}
              to="/"
              sx={{
                display: "flex",
                alignItems: "center",
                height: "100%",
              }}
            >
              <Box
                component="img"
                src="/al-germanist-logo.png"
                alt="ALGermanist"
                onError={(event) => {
                  event.currentTarget.src = "/al-germanist-logo.svg";
                }}
                sx={{
                  display: "block",
                  height: { xs: 80, sm: 66, md: 76, lg: 92 },
                  width: "auto",
                  maxWidth: { xs: 400, sm: 170, md: 205, lg: 245 },
                  objectFit: "contain",
                }}
              />
            </Box>
          </Box>

          {/* Center: Nav Items */}
          {!isMobile && (
            <Box sx={{ flex: 1, display: "flex", justifyContent: "center" }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: { sm: 1, md: 2, lg: 7, xl: 9 },
                }}
              >
                <Button
                  onClick={(event) => setServicesAnchorEl(event.currentTarget)}
                  endIcon={<ExpandMoreIcon />}
                  aria-haspopup="menu"
                  aria-expanded={Boolean(servicesAnchorEl)}
                  sx={{
                    color: "black",
                    fontWeight: "bold",
                    fontSize: { xs: "0.7rem", sm: "1rem" },
                  }}
                >
                  {t("navbar.ourServices")}
                </Button>
                <Menu
                  anchorEl={servicesAnchorEl}
                  open={Boolean(servicesAnchorEl)}
                  onClose={() => setServicesAnchorEl(null)}
                  disableScrollLock
                >
                  {serviceItems.map((service) => (
                    <MenuItem
                      key={service.link}
                      disabled={service.comingSoon}
                      onClick={() => !service.comingSoon && navigateToService(service.link)}
                    >
                      {service.label}{service.comingSoon ? ` (${t("comingSoon")})` : ""}
                    </MenuItem>
                  ))}
                </Menu>
                {navItems.map((item) => {
                  const key = item.key || item.label;
                  return (
                    <Button
                      key={key}
                      component={Link}
                      to={item.link}
                      sx={{
                        color: "black",
                        fontWeight: "bold",
                        fontSize: { xs: "0.7rem", sm: "1rem" },
                        "&:hover": { textDecoration: "underline" },
                      }}
                    >
                      {item.label}
                    </Button>
                  );
                })}
                <Button
                  onClick={(event) => setFeedbackAnchorEl(event.currentTarget)}
                  endIcon={<ExpandMoreIcon />}
                  sx={{ color: "black", fontWeight: "bold", fontSize: { xs: "0.7rem", sm: "1rem" } }}
                >
                  {feedbackLabel}
                </Button>
                <Menu anchorEl={feedbackAnchorEl} open={Boolean(feedbackAnchorEl)} onClose={() => setFeedbackAnchorEl(null)} disableScrollLock>
                  {feedbackItems.map((item) => <MenuItem key={item.link} onClick={() => { setFeedbackAnchorEl(null); navigate(item.link); }}>{item.label}</MenuItem>)}
                </Menu>
              </Box>
            </Box>
          )}

          {/* Right Side: Language Switcher + Auth/Profile */}
          {!isMobile && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <AccessDashboardButton />
              {/* Language Selector */}
              <FormControl size="small" sx={{ minWidth: 100 }}>
                <Select
                  value={selectedLanguage}
                  onChange={(e) => handleLanguageChange(e.target.value)}
                  displayEmpty
                  MenuProps={{ disableScrollLock: true }}
                  sx={{
                    height: 30,
                    borderRadius: "18px",
                    backgroundColor: "#F6F7F8",
                    fontWeight: 600,
                    color: "#333",
                    px: 1,
                    "& .MuiSelect-select": {
                      py: 0.8,
                      display: "flex",
                      alignItems: "center",
                    },
                  }}
                  renderValue={(val) => <LanguageLabel language={val} />}
                >
                  <MenuItem value="ar"><LanguageLabel language="ar" full /></MenuItem>
                  <MenuItem value="de"><LanguageLabel language="de" full /></MenuItem>
                  <MenuItem value="en"><LanguageLabel language="en" full /></MenuItem>
                </Select>
              </FormControl>

              {isLoggedIn ? (
                <>
                  <IconButton
                    onClick={handleProfileMenuOpen}
                    sx={{ color: "white" }}
                  >
                    <Avatar
                      sx={{
                        bgcolor: "#f0ececff",
                        color: "black",
                        padding: 3,
                        width: 40,
                        height: 40,
                        fontWeight: "semi-bold",
                      }}
                    >
                      {getInitials(profileName)}
                    </Avatar>
                  </IconButton>
                  <Menu
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl)}
                    onClose={handleMenuClose}
                    disableScrollLock
                    anchorOrigin={{
                      vertical: "bottom",
                      horizontal: "right",
                    }}
                    transformOrigin={{
                      vertical: "top",
                      horizontal: "right",
                    }}
                  >
                    <MenuItem onClick={handleProfileNavigation}>
                      {t("navbar.profile")}
                    </MenuItem>
                    <MenuItem onClick={handleLogout}>
                      {t("navbar.logout")}
                    </MenuItem>
                  </Menu>
                </>
              ) : (
                <>
                  <Button
                    component={Link}
                    to="/login"
                    sx={{
                      backgroundColor: "white",
                      color: "black",
                      fontWeight: "bold",
                      borderRadius: "20px",
                      px: 2,
                      "&:hover": {
                        backgroundColor: "#d8d8d8ff",
                      },
                    }}
                  >
                    {t("navbar.login")}
                  </Button>
                  <Button
                    component={Link}
                    to="/signup"
                    sx={{
                      backgroundColor: "var(--primary-color)",
                      color: "black",
                      fontWeight: "bold",
                      borderRadius: "20px",
                      px: 3,
                      "&:hover": {
                        backgroundColor: "var(--primary-hover-color)",
                      },
                    }}
                  >
                    {t("navbar.signup")}
                  </Button>
                </>
              )}
            </Box>
          )}

          {/* Mobile Hamburger */}
          {isMobile && (
            <>
              <IconButton
                edge="end"
                color="black"
                onClick={() => setDrawerOpen(true)}
              >
                <MenuIcon sx={{ fontSize: { xs: 22, sm: 24, md: 24 } }} />
              </IconButton>

              <Drawer
                anchor={direction === "rtl" ? "left" : "right"}
                open={drawerOpen}
                onClose={() => setDrawerOpen(false)}
              >
                <Box
                  sx={{
                    width: { xs: 220, sm: 236, md: 240 },
                    py: 2,
                    px: 2,
                    backgroundColor: "white",
                    height: "100%",
                  }}
                >
                  <List>
                    <ListItem button onClick={() => setMobileServicesOpen((open) => !open)}>
                      <ListItemText
                        primary={t("navbar.ourServices")}
                        primaryTypographyProps={{ fontWeight: "bold", fontSize: { xs: 14, sm: 15 } }}
                      />
                      <ExpandMoreIcon
                        sx={{ transform: mobileServicesOpen ? "rotate(180deg)" : "none", transition: "0.2s" }}
                      />
                    </ListItem>
                    <Collapse in={mobileServicesOpen} timeout="auto" unmountOnExit>
                      <List disablePadding>
                        {serviceItems.map((service) => (
                          <ListItem
                            key={service.link}
                            button
                            disabled={service.comingSoon}
                            onClick={() => !service.comingSoon && navigateToService(service.link)}
                            sx={{ px: 3.5 }}
                          >
                            <ListItemText
                              primary={`${service.label}${service.comingSoon ? ` (${t("comingSoon")})` : ""}`}
                              primaryTypographyProps={{ fontSize: { xs: 13, sm: 14 } }}
                            />
                          </ListItem>
                        ))}
                      </List>
                    </Collapse>
                    {navItems.map((item) => (
                      <ListItem
                        key={item.label}
                        button
                        component={Link}
                        to={item.link}
                      >
                        <ListItemText
                          primary={item.label}
                          primaryTypographyProps={{
                            fontWeight: "bold",
                            fontSize: { xs: 14, sm: 15 },
                          }}
                        />
                      </ListItem>
                    ))}
                    <ListItem button onClick={() => setMobileFeedbackOpen((open) => !open)}>
                      <ListItemText primary={feedbackLabel} primaryTypographyProps={{ fontWeight: "bold", fontSize: { xs: 14, sm: 15 } }} />
                      <ExpandMoreIcon sx={{ transform: mobileFeedbackOpen ? "rotate(180deg)" : "none", transition: "0.2s" }} />
                    </ListItem>
                    <Collapse in={mobileFeedbackOpen} timeout="auto" unmountOnExit>
                      <List disablePadding>{feedbackItems.map((item) => <ListItem key={item.link} button onClick={() => { setDrawerOpen(false); navigate(item.link); }} sx={{ px: 3.5 }}><ListItemText primary={item.label} primaryTypographyProps={{ fontSize: { xs: 13, sm: 14 } }} /></ListItem>)}</List>
                    </Collapse>
                  </List>
                  {/* AccessDashboardButton in mobile sidebar */}
                  {isLoggedIn && (
                    <>
                      <Divider sx={{ my: 1 }} />
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          my: 2,
                          transform: { xs: "scale(0.9)", sm: "scale(0.95)" },
                        }}
                      >
                        <AccessDashboardButton />
                      </Box>
                      <Divider sx={{ my: 1 }} />
                    </>
                  )}
                  {isLoggedIn ? (
                    <>
                      <ListItem
                        button
                        onClick={handleProfileNavigation}
                      >
                        <ListItemText
                          primary={t("navbar.profile")}
                          primaryTypographyProps={{
                            fontSize: { xs: 14, sm: 15 },
                          }}
                        />
                      </ListItem>
                      <ListItem button onClick={handleLogout}>
                        <ListItemText
                          primary={t("navbar.logout")}
                          primaryTypographyProps={{
                            fontSize: { xs: 14, sm: 15 },
                          }}
                        />
                      </ListItem>
                    </>
                  ) : (
                    <Box>
                      <ListItem button component={Link} to="/login">
                        <ListItemText
                          primary={t("navbar.login")}
                          primaryTypographyProps={{
                            fontSize: { xs: 14, sm: 15 },
                          }}
                        />
                      </ListItem>
                      <ListItem button component={Link} to="/signup">
                        <ListItemText
                          primary={t("navbar.signup")}
                          primaryTypographyProps={{
                            fontSize: { xs: 14, sm: 15 },
                          }}
                        />
                      </ListItem>
                    </Box>
                  )}
                  {/* Language Toggle as Buttons */}
                  <Divider sx={{ my: 2 }} />
                  <Box
                    sx={{ display: "flex", justifyContent: "center", gap: 1, flexWrap: "wrap" }}
                  >
                    <Button
                      variant={
                        selectedLanguage === "en" ? "contained" : "outlined"
                      }
                      size="small"
                      onClick={() => handleLanguageChange("en")}
                      sx={{
                        minWidth: { xs: 68, sm: 80 },
                        borderRadius: "18px",
                        fontWeight: 600,
                        fontSize: { xs: 12, sm: 13 },
                        px: { xs: 1.25, sm: 1.5 },
                        backgroundColor:
                          selectedLanguage === "en" ? "#F6F7F8" : "white",
                        color: "#333",
                        borderColor: "#F6F7F8",
                        "&:hover": {
                          backgroundColor: "#e0e0e0",
                        },
                      }}
                    >
                      <LanguageLabel language="en" full />
                    </Button>
                    <Button
                      variant={
                        selectedLanguage === "de" ? "contained" : "outlined"
                      }
                      size="small"
                      onClick={() => handleLanguageChange("de")}
                      sx={{
                        minWidth: { xs: 68, sm: 80 },
                        borderRadius: "18px",
                        fontWeight: 600,
                        fontSize: { xs: 12, sm: 13 },
                        px: { xs: 1.25, sm: 1.5 },
                        backgroundColor:
                          selectedLanguage === "de" ? "#F6F7F8" : "white",
                        color: "#333",
                        borderColor: "#F6F7F8",
                        "&:hover": {
                          backgroundColor: "#e0e0e0",
                        },
                      }}
                    >
                      <LanguageLabel language="de" full />
                    </Button>
                    <Button
                      variant={
                        selectedLanguage === "ar" ? "contained" : "outlined"
                      }
                      size="small"
                      onClick={() => handleLanguageChange("ar")}
                      sx={{
                        minWidth: { xs: 68, sm: 80 },
                        borderRadius: "18px",
                        fontWeight: 600,
                        fontSize: { xs: 12, sm: 13 },
                        px: { xs: 1.25, sm: 1.5 },
                        backgroundColor:
                          selectedLanguage === "ar" ? "#F6F7F8" : "white",
                        color: "#333",
                        borderColor: "#F6F7F8",
                        "&:hover": {
                          backgroundColor: "#e0e0e0",
                        },
                      }}
                    >
                      <LanguageLabel language="ar" full />
                    </Button>
                  </Box>
                </Box>
              </Drawer>
            </>
          )}
        </Toolbar>
      </AppBar>
    </>
  );
};

export default NavBar;
